import debounce from "lodash/debounce";
import { useRef, useState, useLayoutEffect, useCallback } from "react";

import type { NavigationItems, NavigationKeys } from "./useNavigationItems";

const SIDEBAR_SPACING = 48;
const PADDING_BOTTOM = 16;
const OVERFLOW_THRESHOLD = 44;
export const LINK_HEIGHT = 44;

interface OverflowResult {
  mainIds: NavigationKeys[];
  overflowIds: NavigationKeys[];
  topRef: React.RefObject<HTMLDivElement | null>;
  navRef: React.RefObject<HTMLDivElement | null>;
  bottomRef: React.RefObject<HTMLDivElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

function measure(element: HTMLElement | null): number {
  return element?.getBoundingClientRect().height ?? 0;
}

function sameKeys(a: NavigationKeys[], b: NavigationKeys[]): boolean {
  return a.length === b.length && a.every((key, i) => key === b[i]);
}

export function useOverflowedItems(
  navigationItems: NavigationItems | null,
): OverflowResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const topRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const [mainIds, setMainIds] = useState<NavigationKeys[]>([]);
  const [overflowIds, setOverflowIds] = useState<NavigationKeys[]>([]);
  const linkHeightRef = useRef<number | null>(null);

  const navigationKeys = (
    navigationItems ? Object.keys(navigationItems) : []
  ) as NavigationKeys[];

  const computeOverflow = useCallback(
    (overflowCount: number) => {
      if (!navigationItems) return;

      const count = Math.min(overflowCount, navigationKeys.length);
      const remaining = [...navigationKeys];
      const overflow: NavigationKeys[] = [];

      while (overflow.length < count) {
        // 1) find the **highest** rank number among remaining
        const maxRank = Math.max(
          ...remaining.map((key) => navigationItems![key].order),
        );
        // 2) pick *one* item from that rank group:
        //    e.g. last in array so you overflow from the bottom
        const idx = remaining
          .map((key) => navigationItems![key].order)
          .lastIndexOf(maxRank);
        const [item] = remaining.splice(idx, 1);
        overflow.push(item);
      }

      overflow.reverse();
      setOverflowIds((prev) => (sameKeys(prev, overflow) ? prev : overflow));
      setMainIds((prev) => (sameKeys(prev, remaining) ? prev : remaining));
    },
    [navigationItems, navigationKeys],
  );

  const visibleCount = navigationKeys.filter((key) => {
    const item = navigationItems![key];
    return !("enabled" in item) || item.enabled !== false;
  }).length;

  useLayoutEffect(() => {
    if (!containerRef.current || navigationKeys.length === 0) return;

    // Handler reads all heights and calls computeOverflow once
    const handler = () => {
      const containerHeight = measure(containerRef.current);

      // we need this guard when we resizing from mobile to desktop width
      if (containerHeight === 0) {
        computeOverflow(0);
        return;
      }

      const topHeight = measure(topRef.current);
      const navHeight = measure(navRef.current);
      const bottomHeight = measure(bottomRef.current);

      if (linkHeightRef.current === null && mainIds.length > 0) {
        const firstLink = navRef.current?.firstElementChild ?? null;
        const measured = measure(firstLink as HTMLElement | null);
        if (measured > 0) linkHeightRef.current = measured;
      }
      const linkHeight = linkHeightRef.current ?? LINK_HEIGHT;

      const chromeHeight =
        // preserve original nav space, plus threshold
        topHeight -
        navHeight +
        bottomHeight +
        PADDING_BOTTOM +
        SIDEBAR_SPACING +
        OVERFLOW_THRESHOLD;
      const fullHeight = chromeHeight + visibleCount * linkHeight;

      if (fullHeight <= containerHeight) {
        computeOverflow(0);
        return;
      }

      computeOverflow(
        Math.ceil((fullHeight + linkHeight - containerHeight) / linkHeight),
      );
    };

    const debounced = debounce(handler, 10, { leading: true, trailing: true });
    const observer = new ResizeObserver(debounced);

    observer.observe(containerRef.current);
    handler();

    return () => {
      observer.disconnect();
      debounced.cancel();
    };
  }, [navigationKeys.length, visibleCount, overflowIds.length, mainIds.length]);

  return { mainIds, overflowIds, topRef, navRef, bottomRef, containerRef };
}
